import React from 'react';
import { FaPeopleLine, 
  FaPeoplePulling,
  FaHandsHoldingChild,
  FaLightbulb,
  FaBullseye,
  FaFaceTired,
  FaFaceFrown,
  FaHeartPulse,
  FaHeartCrack 
 } from "react-icons/fa6";
import { PiCertificateDuotone } from "react-icons/pi";
import { HiMiniAcademicCap } from "react-icons/hi2";
import './About.css';
import { motion } from "framer-motion";
import BlobImage from '../components/BlobImage';
import portrait from "../img/em-b.jpg"
import { NAME } from '../constants';

const About: React.FC = () => {
  return (
    <motion.div className="about">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <div className="about-hero-text">
              <p className="about-hero-subtitle">
                Psicóloga Clínica especializada en terapia cognitivo-conductual
                ayudando a personas a superar
                sus dificultades emocionales y alcanzar su bienestar.
              </p>
            </div>
            <div className="about-hero-image">
              <span className="about-caption"> Licda. Emily <br></br> Gamarro</span>
              <BlobImage
                src={portrait}
                height={200}
                width={200}
                imageHeight={230}
                d="M46.8,-81.5C60.4,-73.3,70.8,-60.1,79,-45.7C87.2,-31.3,93.1,-15.6,92.5,-0.3C91.9,14.9,84.7,29.9,76.4,44C68,58.1,58.5,71.5,45.6,80.2C32.6,88.9,16.3,93,0.6,91.9C-15.1,90.8,-30.1,84.5,-43.9,76.3C-57.7,68.1,-70.3,58,-77.4,44.9C-84.4,31.9,-86,15.9,-85.2,0.4C-84.5,-15.1,-81.4,-30.2,-74.8,-44C-68.2,-57.9,-58.1,-70.5,-45.1,-79C-32,-87.4,-16,-91.7,0.3,-92.3C16.6,-92.8,33.3,-89.7,46.8,-81.5Z"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Bio Section */}
      <section className="section section-light">
        <div className="container">
          <div className="bio-content">
            <div className="bio-text">
              <h2>Mi historia y enfoque</h2>
              <p>
                Estoy en este viaje de ser psicóloga desde hace un tiempo, la psicología me ha permitido ver la vida desde diferentes ángulos y
                me ha regalado la oportunidad de tener relación con la parte más vulnerable del ser humano. Es un privilegio poder tener ese
                contacto con lo más profundo de cada persona.
              </p>
              <p>
                Mi enfoque se basa en la terapia cognitivo-conductual, una metodología
                científicamente validada que se centra en identificar y modificar
                patrones de pensamiento y comportamiento que pueden estar causando
                malestar emocional. Creo firmemente en el potencial de cada persona
                para el cambio y el crecimiento.
              </p>
              <p>
                He tenido el privilegio de acompañar a
                mis pacientes en su proceso de sanación y crecimiento personal.
                Cada historia es única, y cada logro de mis pacientes refuerza mi
                compromiso con esta profesión tan gratificante.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header text-center">
            <h2>Formación y Credenciales</h2>
            <p className="section-subtitle">
              Mi preparación académica y profesional me permite ofrecer
              la mejor atención psicológica basada en evidencia científica
            </p>
          </div>
          <div className="grid grid-2">
            <article className="credential-card">
              <HiMiniAcademicCap className="credential-icon" />
              <div className="credential-content">
                <h3>Formación Académica</h3>
                <ul>
                  <li>Licenciatura en Psicología — Universidad de San Carlos de Guatemala (USAC) <br></br> <strong>Distinción Cum Laude</strong>
                  </li>
                  <li>Entrenamiento en Psicoanálisis Contemporáneo — Tecnológico de Monterrey (Tec) (CNEIP)</li>
                  <li>Diplomado en Gestión de Recursos Humanos — Universidad de San Carlos de Guatemala (USAC) <strong>Distinción Cum Laude</strong></li>
                  <li>Entrenamiento en Manejo de Estrés Postraumático — Universidad del Istmo (UNIS)</li>
                </ul>
              </div>
            </article>
            <article className="credential-card">
              <PiCertificateDuotone  className="credential-icon" />
              <div className="credential-content">
                <h3>Certificaciones</h3>
                <ul>
                  <li>Colegio de Psicólogos de Guatemala — Colegiado No. 15936</li>
                  <li>Certificación en Prevención de la Autolesión y el Suicidio — Universidad de Barcelona (UB)</li>
                  <li>Certificación en Primeros Auxilios Psicológicos — AGAPI (Asociación Guatemalteca de Apoyo Psicológico Integral)</li>
                  
                </ul>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header text-center">
            <h2>Mi Enfoque Terapéutico</h2>
            <p className="section-subtitle">
              Creo en un abordaje integral que combine técnicas basadas en evidencia
              con un trato humano y personalizado
            </p>
          </div>
          <div className="grid grid-3">
            <div className="approach-card">
              <FaBullseye className="approach-icon" />
              <h3>Orientado a Objetivos</h3>
              <p>
                Trabajamos juntos para establecer metas claras y alcanzables,
                con un plan terapéutico estructurado que nos permita medir
                el progreso y ajustar el tratamiento según sea necesario.
              </p>
            </div>
            <div className="approach-card">
              <FaLightbulb  className="approach-icon" />
              <h3>Basado en Evidencia</h3>
              <p>
                Utilizo técnicas y metodologías que han demostrado científicamente
                su efectividad, manteniéndome actualizada con las últimas
                investigaciones en psicología clínica.
              </p>
            </div>
            <div className="approach-card">
              <FaHeartPulse className="approach-icon" />
              <h3>Centrado en la Persona</h3>
              <p>
                Cada persona es única, por lo que adapto mi enfoque a las
                necesidades específicas de cada paciente, creando un ambiente
                seguro y de confianza para el cambio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Specializations */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header text-center">
            <h2>Áreas de Especialización</h2>
            <p className="section-subtitle">
              Me especializo en diversas áreas de la psicología clínica
              para ofrecer una atención integral y especializada
            </p>
          </div>
          <div className="specializations">
            <div className="specialization-item">
              <div className="specialization-icon">
                <FaHeartCrack size={24} />
              </div>
              <div className="specialization-content">
                <h4>Trastornos de Ansiedad</h4>
                <p>Ansiedad generalizada, ataques de pánico, fobias específicas y ansiedad social</p>
              </div>
            </div>
            <div className="specialization-item">
              <div className="specialization-icon">
                <FaFaceFrown size={24} />
              </div>
              <div className="specialization-content">
                <h4>Depresión</h4>
                <p>Episodios depresivos mayores, distimia y trastornos del estado de ánimo</p>
              </div>
            </div>
            <div className="specialization-item">
              <div className="specialization-icon">
                <FaPeoplePulling size={24} />
              </div>
              <div className="specialization-content">
                <h4>Terapia de Pareja</h4>
                <p>Problemas de comunicación, conflictos relacionales y fortalecimiento de vínculos</p>
              </div>
            </div>
            <div className="specialization-item">
              <div className="specialization-icon">
                <FaFaceTired size={24} />
              </div>
              <div className="specialization-content">
                <h4>Estrés y Burnout</h4>
                <p>Manejo del estrés laboral, síndrome de burnout y técnicas de relajación</p>
              </div>
            </div>
            <div className="specialization-item">
              <div className="specialization-icon">
                <FaHandsHoldingChild size={24} />
              </div>
              <div className="specialization-content">
                <h4>Autoestima y Crecimiento Personal</h4>
                <p>Desarrollo de la autoconfianza, habilidades sociales y crecimiento personal</p>
              </div>
            </div>
            <div className="specialization-item">
              <div className="specialization-icon">
                <FaPeopleLine  size={24} />
              </div>
              <div className="specialization-content">
                <h4>Gestión de Recursos Humanos</h4>
                <p>Reclutamiento y selección, onboarding, evaluación de desempeño, clima laboral y cumplimiento legal. </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section section-light">
        <div className="container">
          <div className="philosophy-content">
            <h2 className="text-center">Mi Filosofía de Trabajo</h2>
            <div className="philosophy-text">
              <blockquote>
                "Creo profundamente en la capacidad innata de cada persona para el cambio
                y la sanación. Mi rol como psicóloga no es juzgar o dirigir, sino acompañar,
                guiar y proporcionar las herramientas necesarias para que cada individuo
                pueda descubrir su propio camino hacia el bienestar."
              </blockquote>
              <p className="philosophy-author">— {NAME}</p>
            </div>
            {/* <div className="philosophy-values">
              <div className="value-item">
                <strong>Empatía:</strong> Entiendo que buscar ayuda requiere valentía,
                y me comprometo a crear un espacio seguro y libre de juicios.
              </div>
              <div className="value-item">
                <strong>Profesionalismo:</strong> Mantengo los más altos estándares
                éticos y de confidencialidad en mi práctica clínica.
              </div>
              <div className="value-item">
                <strong>Colaboración:</strong> El proceso terapéutico es un trabajo
                en equipo donde paciente y terapeuta trabajan hacia objetivos comunes.
              </div>
            </div> */}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;